/**
 * Nexa-Core v2 — floating AI companion (Phase 12 refactor).
 *
 * CHANGED vs Phase 8:
 *  • mouse-follow → draggable (framer-motion drag + boundary clamp +
 *    localStorage persist + resize re-clamp).
 *  • Mini options panel → full ChatPanel with LLM round-trip via
 *    POST /api/nexacore/chat (session-only, no DB).
 *  • Visual: 72px orb with central gold medallion + bold 'N' + two
 *    pale-gold blink eyes above. Aura is gold (replaces old teal).
 *  • Inactivity greeting after 150s idle; auto-dismiss after 8s.
 *
 * PRESERVED:
 *  • 4-step tour on first /auditor visit per user.
 *  • Coaching tooltip on `nexacore:coach` CustomEvent (score < 8).
 *  • Hide/Reveal with localStorage persistence.
 *  • prefers-reduced-motion (disables blink / pulse / drag momentum).
 *  • Pointer-events isolation (only the core button intercepts clicks).
 *
 * The orb and panel are rendered in portals-of-composition via position:fixed;
 * the orb is `pointer-events:none` on the wrapper so the UI below isn't
 * blocked. The clickable core is the only `pointer-events:auto` zone.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AnimatePresence,
  animate,
  motion,
  useMotionValue,
} from "framer-motion";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import {
  EyeOff,
  MoreHorizontal,
  RefreshCcw,
  Send,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useWallet } from "../hooks/useWallet";
import { sendChat } from "../lib/nexacore";

// ---------- constants ----------------------------------------------------

// Phase 13: orb grows into a robot capsule (head + body). Width stays at
// 72px, height increases to 100px so there's room for the head sphere +
// body capsule. The chest badge carries the gold 'N' logo.
const ORB_WIDTH = 72;
const ORB_HEIGHT = 100;
// Preserve legacy name for anything that doesn't care about aspect.
const ORB_SIZE = ORB_WIDTH;
const EDGE_PADDING = 10; // min clamp from viewport edges
const DEFAULT_MARGIN_DESKTOP = 24;
const DEFAULT_MARGIN_MOBILE = 16;
const MOBILE_BREAKPOINT = 640;

const INACTIVITY_MS = 150_000; // 2.5 min
const GREETING_AUTO_DISMISS_MS = 8_000;
const COACH_TIMEOUT_MS = 6_000;
const TOUR_DELAY_MS = 800;

const HIDE_LS_KEY = "nxa_core_hidden";
const POS_LS_KEY = "nxa_core_pos";
const CHAT_SESSION_KEY = "nxa_core_chat_session";
const TOUR_LS_KEY = (uid) => `nxa_core_tour_seen_${uid || "anon"}`;

const CHAT_FREE_DAILY_LIMIT = 30;

/** Tour targets (data-testids on the Auditor page). Preserved from Phase 8. */
const TOUR_STEPS = [
  { key: "step1", selector: "auditor-prompt-input" },
  { key: "step2", selector: "auditor-mode-toggle" },
  { key: "step3", selector: "auditor-run-btn" },
  { key: "step4", selector: "auditor-balance" },
];

// ---------- hooks --------------------------------------------------------

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

function useIsMobile() {
  const [mobile, setMobile] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT,
  );
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mobile;
}

function clampPos(x, y, vw, vh, pad = EDGE_PADDING) {
  return {
    x: Math.max(pad, Math.min(vw - ORB_WIDTH - pad, x)),
    y: Math.max(pad, Math.min(vh - ORB_HEIGHT - pad, y)),
  };
}

function defaultPos(isMobile) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = isMobile ? DEFAULT_MARGIN_MOBILE : DEFAULT_MARGIN_DESKTOP;
  return { x: vw - ORB_WIDTH - margin, y: vh - ORB_HEIGHT - margin };
}

/** Draggable position with persistence + boundary clamp. */
function useDraggablePosition(isMobile) {
  const initial = useMemo(() => {
    if (typeof window === "undefined") return { x: 0, y: 0 };
    if (isMobile) return defaultPos(true);
    try {
      const raw = localStorage.getItem(POS_LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Number.isFinite(parsed?.x) && Number.isFinite(parsed?.y)) {
          return clampPos(parsed.x, parsed.y, window.innerWidth, window.innerHeight);
        }
      }
    } catch {
      /* ignore */
    }
    return defaultPos(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // compute once; resize-driven reclamps are handled below

  const x = useMotionValue(initial.x);
  const y = useMotionValue(initial.y);

  // Re-clamp on resize so a dragged orb never ends up off-screen. On
  // mobile we clamp against the preferred default margin (16px) rather than
  // the min padding (10px) so the orb stays snug in the bottom-right corner.
  useEffect(() => {
    const onResize = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const pad = window.innerWidth < MOBILE_BREAKPOINT
        ? DEFAULT_MARGIN_MOBILE
        : EDGE_PADDING;
      const clamped = clampPos(x.get(), y.get(), vw, vh, pad);
      animate(x, clamped.x, { duration: 0.25 });
      animate(y, clamped.y, { duration: 0.25 });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [x, y]);

  // Force mobile fallback position when breakpoint flips to mobile.
  useEffect(() => {
    if (!isMobile) return;
    const d = defaultPos(true);
    animate(x, d.x, { duration: 0.25 });
    animate(y, d.y, { duration: 0.25 });
  }, [isMobile, x, y]);

  const handleDragEnd = useCallback(() => {
    if (isMobile) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const clamped = clampPos(x.get(), y.get(), vw, vh);
    animate(x, clamped.x, { type: "spring", stiffness: 400, damping: 32 });
    animate(y, clamped.y, { type: "spring", stiffness: 400, damping: 32 });
    try {
      localStorage.setItem(POS_LS_KEY, JSON.stringify(clamped));
    } catch {
      /* ignore quota errors */
    }
  }, [isMobile, x, y]);

  return { x, y, handleDragEnd };
}

/** Global inactivity watcher. Fires `greeting=true` after 150s idle;
 *  auto-dismisses after 8s or on next interaction. */
function useInactivityTooltip(enabled) {
  const [showGreeting, setShowGreeting] = useState(false);
  const idleTimer = useRef(0);
  const dismissTimer = useRef(0);

  const arm = useCallback(() => {
    window.clearTimeout(idleTimer.current);
    window.clearTimeout(dismissTimer.current);
    setShowGreeting(false);
    if (!enabled) return;
    idleTimer.current = window.setTimeout(() => {
      setShowGreeting(true);
      dismissTimer.current = window.setTimeout(
        () => setShowGreeting(false),
        GREETING_AUTO_DISMISS_MS,
      );
    }, INACTIVITY_MS);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      window.clearTimeout(idleTimer.current);
      window.clearTimeout(dismissTimer.current);
      setShowGreeting(false);
      return;
    }
    const onInteract = () => arm();
    window.addEventListener("mousemove", onInteract, { passive: true });
    window.addEventListener("keydown", onInteract);
    window.addEventListener("touchstart", onInteract, { passive: true });
    arm();
    return () => {
      window.removeEventListener("mousemove", onInteract);
      window.removeEventListener("keydown", onInteract);
      window.removeEventListener("touchstart", onInteract);
      window.clearTimeout(idleTimer.current);
      window.clearTimeout(dismissTimer.current);
    };
  }, [enabled, arm]);

  const dismissGreeting = useCallback(() => {
    window.clearTimeout(dismissTimer.current);
    setShowGreeting(false);
  }, []);

  return { showGreeting, dismissGreeting };
}

/** In-memory + sessionStorage chat state. Session-only; cleared when the
 *  browser tab closes. */
function useChatSession() {
  const [messages, setMessages] = useState(() => {
    try {
      const raw = sessionStorage.getItem(CHAT_SESSION_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch {
      /* ignore */
    }
    return [];
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(CHAT_SESSION_KEY, JSON.stringify(messages));
    } catch {
      /* ignore quota errors */
    }
  }, [messages]);

  const append = useCallback((msg) => {
    setMessages((cur) => [...cur, msg]);
  }, []);
  const clear = useCallback(() => {
    setMessages([]);
    try {
      sessionStorage.removeItem(CHAT_SESSION_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  return { messages, setMessages, append, clear };
}

// ---------- visual components -------------------------------------------

/** Robot capsule orb — 72x100 SVG. Composition:
 *   • head: dark glass sphere (r=20, cy=22) with 2 pale-gold blink eyes
 *   • neck: thin connector between head and body
 *   • body: capsule-shaped rounded rect (48x56 centered)
 *   • chest badge: gold medallion with bold black 'N' logo
 *   • gold aura via drop-shadow when coaching or celebrating. */
function OrbVisual({ coaching, celebrating, reduced }) {
  const aura = celebrating ? "#FFD700" : coaching ? "#FFD700" : "#D4AF37";
  const blinkDelay = useMemo(() => 4.5 + Math.random() * 2.5, []);

  return (
    <svg
      viewBox="0 0 72 100"
      width={ORB_WIDTH}
      height={ORB_HEIGHT}
      role="img"
      aria-hidden="true"
      style={{
        filter: `drop-shadow(0 0 30px ${aura}66) drop-shadow(0 0 10px ${aura}cc)`,
      }}
    >
      <defs>
        <radialGradient id="nc3-head" cx="35%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#2a2a2a" />
          <stop offset="55%" stopColor="#1a1a1a" />
          <stop offset="100%" stopColor="#0d0d0d" />
        </radialGradient>
        <radialGradient id="nc3-body" cx="35%" cy="25%" r="80%">
          <stop offset="0%" stopColor="#262626" />
          <stop offset="55%" stopColor="#161616" />
          <stop offset="100%" stopColor="#0a0a0a" />
        </radialGradient>
        <radialGradient id="nc3-gold" cx="40%" cy="35%" r="70%">
          <stop offset="0%" stopColor="#FFE89F" />
          <stop offset="55%" stopColor="#D4AF37" />
          <stop offset="100%" stopColor="#8a6d1e" />
        </radialGradient>
        <linearGradient id="nc3-ring" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#F4D77A" />
          <stop offset="55%" stopColor="#D4AF37" />
          <stop offset="100%" stopColor="#8a6d1e" />
        </linearGradient>
        <linearGradient id="nc3-body-highlight" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.14)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
      </defs>

      {/* --- BODY (capsule) ------------------------------------------- */}
      <rect
        x="12"
        y="42"
        width="48"
        height="56"
        rx="22"
        fill="url(#nc3-body)"
        stroke="url(#nc3-ring)"
        strokeWidth="1.5"
      />
      {/* Body highlight (glossy top sheen) */}
      <rect
        x="14"
        y="44"
        width="44"
        height="28"
        rx="20"
        fill="url(#nc3-body-highlight)"
      />
      {/* Rotating decorative arc on body */}
      <g
        style={{
          transformOrigin: "36px 70px",
          animation: reduced
            ? undefined
            : "nc-spin-fast 24s linear infinite reverse",
        }}
      >
        <path
          d="M 20 70 A 16 16 0 0 1 52 70"
          fill="none"
          stroke="#D4AF37"
          strokeWidth="0.6"
          strokeDasharray="3 5"
          opacity="0.45"
        />
      </g>

      {/* --- CHEST BADGE (gold medallion with 'N') -------------------- */}
      <circle cx="36" cy="70" r="14" fill="url(#nc3-gold)" />
      <circle
        cx="36"
        cy="70"
        r="14"
        fill="none"
        stroke="#8a6d1e"
        strokeWidth="0.6"
        opacity="0.9"
      />
      <text
        x="36"
        y="76"
        textAnchor="middle"
        fontFamily="'Fraunces', 'Inter', system-ui, sans-serif"
        fontWeight="900"
        fontSize="17"
        fill="#000000"
        style={{ letterSpacing: "-0.05em" }}
      >
        N
      </text>

      {/* --- NECK --------------------------------------------------- */}
      <rect
        x="31"
        y="38"
        width="10"
        height="8"
        rx="3"
        fill="#141414"
        stroke="url(#nc3-ring)"
        strokeWidth="0.8"
        opacity="0.9"
      />

      {/* --- HEAD (sphere) ------------------------------------------ */}
      <circle cx="36" cy="22" r="20" fill="url(#nc3-head)" />
      <circle
        cx="36"
        cy="22"
        r="20"
        fill="none"
        stroke="url(#nc3-ring)"
        strokeWidth="1.5"
        opacity="0.9"
      />
      {/* Rotating decorative arc on head */}
      <g
        style={{
          transformOrigin: "36px 22px",
          animation: reduced ? undefined : "nc-spin-slow 60s linear infinite",
        }}
      >
        <circle
          cx="36"
          cy="22"
          r="22"
          fill="none"
          stroke="#D4AF37"
          strokeWidth="0.6"
          strokeDasharray="3 5"
          opacity="0.5"
        />
      </g>

      {/* Blink eyes */}
      <motion.ellipse
        cx="29"
        cy="22"
        rx="2"
        ry="2.6"
        fill="#F4D77A"
        opacity="0.95"
        animate={reduced ? { scaleY: 1 } : { scaleY: [1, 0.1, 1] }}
        transition={
          reduced
            ? { duration: 0 }
            : {
                duration: 0.26,
                repeat: Infinity,
                repeatDelay: blinkDelay,
                ease: "easeOut",
                times: [0, 0.5, 1],
              }
        }
        style={{ transformOrigin: "29px 22px" }}
      />
      <motion.ellipse
        cx="43"
        cy="22"
        rx="2"
        ry="2.6"
        fill="#F4D77A"
        opacity="0.95"
        animate={reduced ? { scaleY: 1 } : { scaleY: [1, 0.1, 1] }}
        transition={
          reduced
            ? { duration: 0 }
            : {
                duration: 0.26,
                repeat: Infinity,
                repeatDelay: blinkDelay,
                ease: "easeOut",
                times: [0, 0.5, 1],
              }
        }
        style={{ transformOrigin: "43px 22px" }}
      />
    </svg>
  );
}

/** Celebration speech bubble — pops in when score >= 8.0. Anchored to the
 *  side of the orb that has more free space. */
function CelebrationBubble({ side, label }) {
  const anchorClass =
    side === "left"
      ? "right-full mr-3 top-1/2 -translate-y-1/2"
      : "left-full ml-3 top-1/2 -translate-y-1/2";
  const tailClass =
    side === "left"
      ? "absolute -right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 rotate-45 bg-[#1a1a1a] border-t border-r border-[#D4AF37]/55"
      : "absolute -left-1.5 top-1/2 -translate-y-1/2 h-3 w-3 rotate-45 bg-[#1a1a1a] border-b border-l border-[#D4AF37]/55";
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.7, y: 6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.85, y: 0 }}
      transition={{
        type: "spring",
        stiffness: 420,
        damping: 22,
      }}
      className={
        "absolute pointer-events-none whitespace-nowrap rounded-full border border-[#D4AF37]/55 bg-[#1a1a1a] px-3.5 py-1.5 text-[13px] font-semibold text-[#F4D77A] shadow-[0_0_24px_rgba(212,175,55,0.4)] " +
        anchorClass
      }
      data-testid="nexacore-celebrate-bubble"
    >
      {label}
      <span aria-hidden className={tailClass} />
    </motion.div>
  );
}

/** Shared tooltip bubble (used for tour, coaching, inactivity greeting). */
function Tooltip({ title, body, footer, testId, anchor = "bottom" }) {
  const anchorStyle =
    anchor === "bottom"
      ? "absolute bottom-[86px] right-0"
      : "absolute top-[86px] right-0";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.96 }}
      transition={{ duration: 0.22, ease: [0.22, 0.9, 0.36, 1] }}
      className={`pointer-events-auto ${anchorStyle} w-[280px] rounded-2xl border border-white/12 bg-[#0b0c11]/95 backdrop-blur-xl px-4 py-3 shadow-[0_20px_60px_-25px_rgba(0,0,0,0.9)]`}
      style={{
        boxShadow:
          "0 20px 60px -25px rgba(0,0,0,0.9), 0 0 0 1px rgba(212,175,55,0.15)",
      }}
      role="dialog"
      data-testid={testId}
    >
      {title ? (
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]/80">
          {title}
        </div>
      ) : null}
      <div className="mt-1 text-sm leading-relaxed text-white/90">{body}</div>
      {footer ? (
        <div className="mt-3 flex items-center justify-between gap-2">
          {footer}
        </div>
      ) : null}
      <div
        aria-hidden
        className={
          "absolute h-3 w-3 rotate-45 bg-[#0b0c11] border-white/12 " +
          (anchor === "bottom"
            ? "-bottom-1.5 right-6 border-r border-b"
            : "-top-1.5 right-6 border-l border-t")
        }
      />
    </motion.div>
  );
}

/** Measure a target element by data-testid for the tour highlight. */
function useTargetRect(selector) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!selector) {
      setRect(null);
      return;
    }
    let raf = 0;
    const measure = () => {
      const el = document.querySelector(`[data-testid="${selector}"]`);
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({ x: r.x, y: r.y, w: r.width, h: r.height });
    };
    measure();
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    const t = setInterval(measure, 500);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
      clearInterval(t);
      cancelAnimationFrame(raf);
    };
  }, [selector]);
  return rect;
}

function TourHighlight({ rect }) {
  if (!rect) return null;
  const pad = 8;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="pointer-events-none fixed z-[55] rounded-xl"
      style={{
        left: rect.x - pad,
        top: rect.y - pad,
        width: rect.w + pad * 2,
        height: rect.h + pad * 2,
        boxShadow:
          "0 0 0 2px rgba(212,175,55,0.9), 0 0 24px 6px rgba(212,175,55,0.35)",
        borderRadius: 12,
      }}
      data-testid="nexacore-tour-highlight"
    />
  );
}

/** Tiny markdown renderer — handles \n → <br>, bullet lines starting with
 *  "- " or "* ", and preserves inline `code` and **bold**. No external dep.
 *  Deliberately minimal: we trust the LLM response not to inject HTML. */
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const out = [];
  let bulletBuffer = [];

  const flushBullets = () => {
    if (bulletBuffer.length === 0) return;
    out.push(
      <ul key={`ul-${out.length}`} className="my-1 ml-4 list-disc space-y-0.5">
        {bulletBuffer.map((b, i) => (
          <li key={i}>{inline(b)}</li>
        ))}
      </ul>,
    );
    bulletBuffer = [];
  };

  const inline = (s) => {
    // bold **x**
    const boldParts = s.split(/(\*\*[^*]+\*\*)/g);
    return boldParts.map((p, i) => {
      if (p.startsWith("**") && p.endsWith("**")) {
        return (
          <strong key={i} className="text-white">
            {p.slice(2, -2)}
          </strong>
        );
      }
      // inline code `x`
      const codeParts = p.split(/(`[^`]+`)/g);
      return (
        <span key={i}>
          {codeParts.map((c, j) =>
            c.startsWith("`") && c.endsWith("`") ? (
              <code
                key={j}
                className="rounded bg-white/10 px-1 py-[1px] font-mono text-[0.85em] text-[#F4D77A]"
              >
                {c.slice(1, -1)}
              </code>
            ) : (
              c
            ),
          )}
        </span>
      );
    });
  };

  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      bulletBuffer.push(trimmed.slice(2));
    } else {
      flushBullets();
      if (trimmed === "") {
        out.push(<div key={`sp-${i}`} className="h-2" aria-hidden />);
      } else {
        out.push(<p key={`p-${i}`}>{inline(line)}</p>);
      }
    }
  });
  flushBullets();
  return out;
}

// ---------- ChatPanel ----------------------------------------------------

function ChatPanel({
  isMobile,
  onClose,
  onHideCore,
  onRestartTour,
  reduced,
}) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isPro = user?.plan === "pro";

  const { messages, append, clear } = useChatSession();
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(null); // null = unlimited / unknown
  const [showMenu, setShowMenu] = useState(false);
  const listRef = useRef(null);
  const textareaRef = useRef(null);

  const lang = i18n.resolvedLanguage || "en";

  // Welcome bubble is virtual: rendered when `messages` is empty so it
  // follows the current language and never has to live in sessionStorage.
  const welcomeBubble = useMemo(
    () => ({ role: "assistant", content: t("nexacore.chat.welcome") }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, lang],
  );
  const renderedMessages = messages.length === 0 ? [welcomeBubble] : messages;

  // Autoscroll to the latest message.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [renderedMessages.length, loading]);

  // Autofocus the textarea when the panel mounts.
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const canSend = draft.trim().length > 0 && !loading;

  const onSend = useCallback(async () => {
    if (!canSend) return;
    const text = draft.trim();
    if (text.length > 2000) return;

    // Add the user turn immediately
    append({ role: "user", content: text });
    setDraft("");
    setLoading(true);

    // Build the history payload (replay prior turns + the new user turn).
    // If messages was empty we don't need to include the virtual welcome.
    const historyPayload = [
      ...messages.map((m) => ({ role: m.role, content: m.content })),
      { role: "user", content: text },
    ];

    const res = await sendChat(historyPayload, lang);
    setLoading(false);

    if (!res.ok) {
      if (res.status === 429) {
        toast.error(t("nexacore.chat.quota_exceeded_title"), {
          description: t("nexacore.chat.quota_exceeded_body"),
        });
        return;
      }
      toast.error(t("nexacore.chat.error"));
      return;
    }

    append({
      role: "assistant",
      content: res.data.reply,
      meta: { fallback: !!res.data.fallback, elapsed_ms: res.data.elapsed_ms },
    });
    if (res.data.remaining !== undefined) {
      setRemaining(res.data.remaining);
    }
  }, [canSend, draft, append, messages, lang, t]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const quotaFooter = isPro
    ? t("nexacore.chat.unlimited")
    : remaining == null
      ? t("nexacore.chat.quota_remaining", { count: CHAT_FREE_DAILY_LIMIT })
      : t("nexacore.chat.quota_remaining", { count: remaining });

  // Positioning:
  //  • Mobile: fullscreen modal with backdrop
  //  • Desktop: fixed 380x520 panel in the bottom-right
  const containerClass = isMobile
    ? "fixed inset-0 z-[70] flex flex-col"
    : "fixed bottom-6 right-6 z-[70] flex h-[520px] w-[380px] flex-col";

  const Backdrop = isMobile ? (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[65] bg-black/70 backdrop-blur-xl"
      onClick={onClose}
      aria-hidden
    />
  ) : null;

  return (
    <>
      {Backdrop}
      <motion.div
        initial={reduced ? { opacity: 0 } : { opacity: 0, y: 20, scale: 0.96 }}
        animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
        exit={reduced ? { opacity: 0 } : { opacity: 0, y: 20, scale: 0.96 }}
        transition={{ duration: 0.22, ease: [0.22, 0.9, 0.36, 1] }}
        className={
          containerClass +
          " overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-[#0b0c11]/95 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)] backdrop-blur-2xl"
        }
        data-testid="nexacore-chat-panel"
        role="dialog"
        aria-label={t("nexacore.chat.title")}
      >
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
          <div className="relative flex h-7 w-7 items-center justify-center">
            <svg viewBox="0 0 72 72" width="28" height="28" aria-hidden>
              <circle cx="36" cy="36" r="34" fill="#0d0d0d" />
              <circle
                cx="36"
                cy="36"
                r="34"
                fill="none"
                stroke="#D4AF37"
                strokeWidth="2.5"
              />
              <circle cx="36" cy="40" r="18" fill="#D4AF37" />
              <text
                x="36"
                y="47"
                textAnchor="middle"
                fontFamily="'Fraunces', 'Inter', system-ui, sans-serif"
                fontWeight="900"
                fontSize="22"
                fill="#000"
                style={{ letterSpacing: "-0.05em" }}
              >
                N
              </text>
            </svg>
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold tracking-tight text-[#F4D77A]">
              {t("nexacore.chat.title")}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">
              AI Assistant
            </div>
          </div>

          {/* Language dropdown (3-way toggle; also updates app i18n) */}
          <div
            className="inline-flex rounded-full border border-white/10 bg-white/[0.04] p-0.5"
            data-testid="nexacore-chat-lang"
          >
            {["en", "es", "hi"].map((lk) => {
              const active = lang === lk;
              return (
                <button
                  key={lk}
                  type="button"
                  onClick={() => i18n.changeLanguage(lk)}
                  className={
                    "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase transition-colors " +
                    (active
                      ? "bg-[#D4AF37] text-black"
                      : "text-white/55 hover:text-white")
                  }
                  data-testid={`nexacore-chat-lang-${lk}`}
                >
                  {lk === "hi" ? "हि" : lk}
                </button>
              );
            })}
          </div>

          {/* Menu (hide + restart tour + clear) */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowMenu((v) => !v)}
              aria-label="Menu"
              data-testid="nexacore-chat-menu-btn"
              className="rounded-md p-1.5 text-white/55 hover:text-white hover:bg-white/5"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
            <AnimatePresence>
              {showMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-9 w-48 overflow-hidden rounded-xl border border-white/10 bg-[#0b0c11]/95 shadow-2xl backdrop-blur-xl"
                  data-testid="nexacore-chat-menu"
                  onMouseLeave={() => setShowMenu(false)}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowMenu(false);
                      onRestartTour?.();
                    }}
                    data-testid="nexacore-menu-restart-tour"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-white/85 hover:bg-white/5"
                  >
                    <RefreshCcw className="h-3.5 w-3.5" />
                    {t("nexacore.menu.restart_tour")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowMenu(false);
                      clear();
                    }}
                    data-testid="nexacore-menu-clear"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-white/85 hover:bg-white/5"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    {t("nexacore.chat.clear")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowMenu(false);
                      onHideCore?.();
                    }}
                    data-testid="nexacore-menu-hide"
                    className="flex w-full items-center gap-2 border-t border-white/10 px-3 py-2 text-left text-sm text-white/70 hover:bg-white/5 hover:text-white"
                  >
                    <EyeOff className="h-3.5 w-3.5" />
                    {t("nexacore.menu.hide")}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label={t("nexacore.chat.close")}
            data-testid="nexacore-chat-close"
            className="rounded-md p-1.5 text-white/55 hover:text-white hover:bg-white/5"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Messages */}
        <div
          ref={listRef}
          className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
          data-testid="nexacore-chat-messages"
        >
          {renderedMessages.map((m, i) => (
            <div
              key={i}
              className={
                "flex " +
                (m.role === "user" ? "justify-end" : "justify-start")
              }
              data-testid={`nexacore-chat-msg-${m.role}`}
            >
              <div
                className={
                  "max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed break-words " +
                  (m.role === "user"
                    ? "bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-white"
                    : "bg-white/[0.05] border border-white/10 text-white/90")
                }
              >
                {m.role === "assistant" ? renderMarkdown(m.content) : m.content}
                {m.role === "assistant" && m.meta?.fallback ? (
                  <div className="mt-1 text-[10px] uppercase tracking-wider text-amber-400/80">
                    fallback
                  </div>
                ) : null}
              </div>
            </div>
          ))}
          {loading ? (
            <div className="flex justify-start" data-testid="nexacore-chat-loading">
              <div className="rounded-2xl border border-white/10 bg-white/[0.05] px-3.5 py-2">
                <div className="flex items-center gap-1">
                  {[0, 1, 2].map((i) => (
                    <motion.span
                      key={i}
                      className="inline-block h-1.5 w-1.5 rounded-full bg-[#D4AF37]"
                      animate={reduced ? {} : { opacity: [0.3, 1, 0.3] }}
                      transition={
                        reduced
                          ? { duration: 0 }
                          : {
                              duration: 1.1,
                              repeat: Infinity,
                              delay: i * 0.18,
                              ease: "easeInOut",
                            }
                      }
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Input */}
        <div className="border-t border-white/10 px-3 py-3">
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value.slice(0, 2000))}
              onKeyDown={onKeyDown}
              placeholder={t("nexacore.chat.placeholder")}
              rows={1}
              className="nxa-input min-h-[40px] max-h-24 resize-none text-sm"
              disabled={loading}
              data-testid="nexacore-chat-input"
              aria-label={t("nexacore.chat.placeholder")}
            />
            <button
              type="button"
              onClick={onSend}
              disabled={!canSend}
              className={
                "inline-flex h-10 items-center justify-center gap-1.5 rounded-xl px-3 text-sm font-semibold transition-opacity " +
                (canSend
                  ? "bg-gradient-to-b from-[#F4D77A] via-[#D4AF37] to-[#A8862B] text-[#0a0a0d]"
                  : "bg-white/5 text-white/30 cursor-not-allowed")
              }
              data-testid="nexacore-chat-send"
              aria-label={t("nexacore.chat.send")}
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-1.5 flex items-center justify-between text-[10px] text-white/35">
            <span data-testid="nexacore-chat-quota">{quotaFooter}</span>
            <span>{t("nexacore.chat.chars_count", { count: draft.length })}</span>
          </div>
          <div className="mt-0.5 text-center text-[9px] uppercase tracking-[0.18em] text-white/25">
            {t("nexacore.chat.poweredBy")}
          </div>
        </div>
      </motion.div>
    </>
  );
}

// ---------- NexaCore main ------------------------------------------------

export function NexaCore() {
  const { t } = useTranslation();
  const { user, isAuthed } = useAuth();
  // Keep useWallet imported so Phase 7 wallet-driven coaching event timing
  // stays consistent (no-op subscription — harmless if useWallet is light).
  useWallet();
  const location = useLocation();
  const reduced = usePrefersReducedMotion();
  const isMobile = useIsMobile();

  // ----- hide state (persisted) -----
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(HIDE_LS_KEY) === "1";
    } catch {
      return false;
    }
  });
  const setHiddenPersist = useCallback((v) => {
    setHidden(v);
    try {
      localStorage.setItem(HIDE_LS_KEY, v ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, []);

  // ----- draggable position -----
  const { x, y, handleDragEnd } = useDraggablePosition(isMobile);

  // ----- panel open state -----
  const [panelOpen, setPanelOpen] = useState(false);

  // ----- tour (preserved from Phase 8) -----
  const [tourActive, setTourActive] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const tourKey = useMemo(() => TOUR_LS_KEY(user?.id), [user?.id]);

  const startTour = useCallback(() => {
    setTourStep(0);
    setTourActive(true);
  }, []);
  const endTour = useCallback(
    (persist = true) => {
      setTourActive(false);
      setTourStep(0);
      if (persist) {
        try {
          localStorage.setItem(tourKey, "1");
        } catch {
          /* ignore */
        }
      }
    },
    [tourKey],
  );

  useEffect(() => {
    if (!isAuthed || hidden) return;
    if (!location.pathname.startsWith("/auditor")) return;
    let seen = false;
    try {
      seen = localStorage.getItem(tourKey) === "1";
    } catch {
      /* ignore */
    }
    if (seen) return;
    const t0 = setTimeout(() => startTour(), TOUR_DELAY_MS);
    return () => clearTimeout(t0);
  }, [isAuthed, hidden, location.pathname, tourKey, startTour]);

  useEffect(() => {
    if (!tourActive) return;
    const onKey = (e) => {
      if (e.key === "Escape") endTour(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tourActive, endTour]);

  useEffect(() => {
    const onRestart = () => {
      try {
        localStorage.removeItem(tourKey);
      } catch {
        /* ignore */
      }
      startTour();
    };
    window.addEventListener("nexacore:restart-tour", onRestart);
    return () =>
      window.removeEventListener("nexacore:restart-tour", onRestart);
  }, [tourKey, startTour]);

  const currentStepDef = tourActive ? TOUR_STEPS[tourStep] : null;
  const targetRect = useTargetRect(currentStepDef?.selector);
  useEffect(() => {
    if (!currentStepDef) return;
    const el = document.querySelector(
      `[data-testid="${currentStepDef.selector}"]`,
    );
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({
        behavior: reduced ? "auto" : "smooth",
        block: "center",
      });
    }
  }, [currentStepDef, reduced]);

  // ----- coaching (preserved) -----
  const [coach, setCoach] = useState(false);
  const coachTimerRef = useRef(0);
  useEffect(() => {
    const onCoach = (e) => {
      const score = Number(e?.detail?.score);
      if (!Number.isFinite(score) || score >= 8.0) return;
      if (hidden) return;
      setCoach(true);
      window.clearTimeout(coachTimerRef.current);
      coachTimerRef.current = window.setTimeout(
        () => setCoach(false),
        COACH_TIMEOUT_MS,
      );
    };
    window.addEventListener("nexacore:coach", onCoach);
    return () => {
      window.removeEventListener("nexacore:coach", onCoach);
      window.clearTimeout(coachTimerRef.current);
    };
  }, [hidden]);

  // ----- celebration (Phase 13) -----
  // Dispatched from Auditor.jsx when a NON-bounced audit scores >= 8.0.
  // Triggers (a) a small confetti burst local to the orb and (b) a
  // short-lived speech bubble next to the orb. Coexists with the big
  // Phase 7 confetti owned by CertificationOverlay (score >= 8.9).
  const [celebrate, setCelebrate] = useState(false);
  const celebrateTimerRef = useRef(0);
  useEffect(() => {
    const onCelebrate = (e) => {
      if (hidden || reduced) return;
      const score = Number(e?.detail?.score);
      if (!Number.isFinite(score) || score < 8.0) return;

      // 1) Local confetti burst from the orb's current position
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const centerX = (x.get() + ORB_WIDTH / 2) / vw;
      const centerY = (y.get() + ORB_HEIGHT / 2) / vh;
      try {
        confetti({
          particleCount: 40,
          spread: 60,
          startVelocity: 25,
          ticks: 150,
          colors: ["#D4AF37", "#FFD700", "#F4D77A"],
          origin: { x: centerX, y: centerY },
          zIndex: 55,
          disableForReducedMotion: true,
        });
      } catch {
        /* ignore if canvas-confetti unavailable in this environment */
      }

      // 2) Speech bubble for 3.5s
      setCelebrate(true);
      window.clearTimeout(celebrateTimerRef.current);
      celebrateTimerRef.current = window.setTimeout(
        () => setCelebrate(false),
        3500,
      );
    };
    window.addEventListener("nexacore:celebrate", onCelebrate);
    return () => {
      window.removeEventListener("nexacore:celebrate", onCelebrate);
      window.clearTimeout(celebrateTimerRef.current);
    };
  }, [hidden, reduced, x, y]);

  // ----- inactivity greeting (new) -----
  const inactivityEnabled =
    isAuthed && !hidden && !tourActive && !panelOpen && !reduced && !coach;
  const { showGreeting, dismissGreeting } = useInactivityTooltip(
    inactivityEnabled,
  );

  // ----- open/close panel -----
  const openPanel = useCallback(() => {
    setPanelOpen(true);
    setCoach(false);
    dismissGreeting();
  }, [dismissGreeting]);
  const closePanel = useCallback(() => setPanelOpen(false), []);

  // ----- render guards -----
  if (!isAuthed) return null;

  if (hidden) {
    return (
      <button
        type="button"
        onClick={() => setHiddenPersist(false)}
        className="fixed bottom-24 right-4 z-40 inline-flex items-center gap-1 rounded-full border border-white/12 bg-[#0b0c11]/90 px-3 py-1.5 text-xs text-white/70 hover:text-white transition-colors"
        data-testid="nexacore-reveal-btn"
        aria-label={t("nexacore.aria.label")}
      >
        <Sparkles className="h-3 w-3 text-[#D4AF37]" />
        {t("nexacore.reveal")}
      </button>
    );
  }

  const showCoach = coach && !tourActive && !panelOpen;
  const showInactivity = showGreeting && !tourActive && !panelOpen && !showCoach;
  const stepIndex = tourStep + 1;
  const stepTotal = TOUR_STEPS.length;
  const isLastStep = tourStep === TOUR_STEPS.length - 1;
  const currentStepBody =
    tourActive && currentStepDef ? t(`nexacore.tour.${currentStepDef.key}`) : "";

  return (
    <>
      {/* Tour highlight ring */}
      <AnimatePresence>
        {tourActive && targetRect ? <TourHighlight rect={targetRect} /> : null}
      </AnimatePresence>

      {/* ChatPanel (fullscreen on mobile / anchored popover on desktop) */}
      <AnimatePresence>
        {panelOpen && (
          <ChatPanel
            isMobile={isMobile}
            reduced={reduced}
            onClose={closePanel}
            onHideCore={() => {
              closePanel();
              setHiddenPersist(true);
            }}
            onRestartTour={() => {
              closePanel();
              try {
                localStorage.removeItem(tourKey);
              } catch {
                /* ignore */
              }
              startTour();
            }}
          />
        )}
      </AnimatePresence>

      {/* Orb — fixed, draggable (desktop only). Wrapper is pointer-events:
          none; only the inner core button accepts clicks. Phase 13: taller
          (72x100) to accommodate the robot capsule silhouette. */}
      <motion.div
        className="fixed left-0 top-0 z-40 select-none"
        style={{
          x,
          y,
          width: ORB_WIDTH,
          height: ORB_HEIGHT,
          pointerEvents: "none",
          willChange: "transform",
          touchAction: isMobile ? "auto" : "none",
        }}
        drag={!isMobile && !reduced}
        dragMomentum={false}
        dragElastic={0.08}
        onDragEnd={handleDragEnd}
        data-testid="nexacore-orb"
        data-draggable={!isMobile && !reduced}
      >
        <div className="relative h-full w-full">
          {/* Aura pulse */}
          <motion.div
            aria-hidden
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              background: showCoach
                ? "radial-gradient(circle at 50% 50%, rgba(255,215,0,0.4), transparent 70%)"
                : "radial-gradient(circle at 50% 50%, rgba(212,175,55,0.35), transparent 70%)",
            }}
            animate={reduced ? { opacity: 0.85 } : { opacity: [0.5, 0.95, 0.5] }}
            transition={
              reduced
                ? { duration: 0 }
                : {
                    duration: showCoach ? 1.5 : 2.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }
            }
          />

          {/* Interactive core button */}
          <motion.button
            type="button"
            onClick={openPanel}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openPanel();
              }
            }}
            aria-label={t("nexacore.aria.label")}
            data-testid="nexacore-core-btn"
            className="absolute inset-0 flex items-center justify-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]/70"
            style={{
              pointerEvents: "auto",
              background: "transparent",
              border: 0,
              cursor: isMobile || reduced ? "pointer" : "grab",
            }}
            whileHover={reduced ? {} : { scale: 1.05 }}
            whileTap={reduced ? {} : { scale: 0.95 }}
            animate={
              reduced
                ? { rotate: 0 }
                : showCoach
                  ? { rotate: [0, -5, 5, -3, 0], scale: [1, 1.06, 1] }
                  : { rotate: 0, scale: 1 }
            }
            transition={
              reduced
                ? { duration: 0 }
                : showCoach
                  ? { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
                  : { duration: 0.3 }
            }
          >
            <OrbVisual coaching={showCoach} celebrating={celebrate} reduced={reduced} />
          </motion.button>

          {/* Celebration speech bubble (Phase 13) — anchors to the side with
              more horizontal room so it never clips off-screen. */}
          <AnimatePresence>
            {celebrate ? (
              <CelebrationBubble
                side={x.get() + ORB_WIDTH / 2 > window.innerWidth / 2 ? "left" : "right"}
                label={t("nexacore.celebrate.bubble")}
              />
            ) : null}
          </AnimatePresence>

          {/* Coaching tooltip */}
          <AnimatePresence>
            {showCoach && (
              <Tooltip
                title={t("nexacore.coach.title")}
                testId="nexacore-coach-tooltip"
                body={<span>{t("nexacore.coach.low_score")}</span>}
                footer={
                  <button
                    type="button"
                    onClick={() => setCoach(false)}
                    data-testid="nexacore-coach-dismiss"
                    className="ml-auto text-xs text-white/60 hover:text-white"
                  >
                    {t("nexacore.dismiss")}
                  </button>
                }
              />
            )}
          </AnimatePresence>

          {/* Tour tooltip */}
          <AnimatePresence>
            {tourActive && (
              <Tooltip
                title={`Nexa-Core · ${stepIndex}/${stepTotal}`}
                testId={`nexacore-tour-tooltip-${stepIndex}`}
                body={<span>{currentStepBody}</span>}
                footer={
                  <>
                    <button
                      type="button"
                      onClick={() => endTour(true)}
                      data-testid="nexacore-tour-skip"
                      className="text-xs text-white/55 hover:text-white/85"
                    >
                      {t("nexacore.tour.skip")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (isLastStep) endTour(true);
                        else setTourStep((s) => s + 1);
                      }}
                      data-testid="nexacore-tour-next"
                      className="rounded-full px-3 py-1 text-xs font-semibold text-[#0a0a0d] bg-gradient-to-b from-[#F4D77A] via-[#D4AF37] to-[#A8862B]"
                    >
                      {isLastStep ? t("nexacore.tour.finish") : t("nexacore.tour.next")}
                    </button>
                  </>
                }
              />
            )}
          </AnimatePresence>

          {/* Inactivity greeting tooltip */}
          <AnimatePresence>
            {showInactivity && (
              <Tooltip
                title={t("nexacore.coach.title")}
                testId="nexacore-inactivity-tooltip"
                body={<span>{t("nexacore.inactivity.greeting")}</span>}
                footer={
                  <>
                    <button
                      type="button"
                      onClick={dismissGreeting}
                      data-testid="nexacore-inactivity-dismiss"
                      className="text-xs text-white/55 hover:text-white/85"
                    >
                      {t("nexacore.dismiss")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        dismissGreeting();
                        openPanel();
                      }}
                      data-testid="nexacore-inactivity-open"
                      className="rounded-full px-3 py-1 text-xs font-semibold text-[#0a0a0d] bg-gradient-to-b from-[#F4D77A] via-[#D4AF37] to-[#A8862B]"
                    >
                      {t("nexacore.chat.title")}
                    </button>
                  </>
                }
              />
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </>
  );
}

export default NexaCore;
